import { Grid, TextInput } from '@mantine/core';
import { Text, SegmentedControl, Input, Textarea, Title, Button, Modal, Group, Loader } from '@mantine/core';
import { PROPOSALACTIONS } from '../../reducers/proposalReducer';
import { useProposal } from '../../context/ProposalContext';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { IconRotate2 } from '@tabler/icons-react';
import { useGeneration } from '@/hooks/useGeneration';
import { defaultFeeTextEn, defaultFeeTextPl } from '../SecondTab/defaultFeeText';

interface CaseOverviewProps {
  showRegenerateButton?: boolean;
  onRegenerateClick?: () => void;
}

export default function CaseOverview({ showRegenerateButton: externalShowRegenerate, onRegenerateClick }: CaseOverviewProps) {
  const { proposal, dispatchProposal, dispatchUserFeedback, dispatchOtherData, userFeedback } = useProposal();
  const { title, language, client, sector, proposalDate } = proposal;
  const { t } = useTranslation();
  const [dateString, setDateString] = useState<string>(
    proposalDate instanceof Date
      ? proposalDate.toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10)
  );
  const [dateError, setDateError] = useState<string | null>(null);
  const [showRegenerateButton, setShowRegenerateButton] = useState(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [originalLanguage, setOriginalLanguage] = useState<string>('');
  const prevLanguageRef = useRef<string>(language);

  const { withService, generateAll } = useGeneration({
    dispatchProposal,
    dispatchUserFeedback,
    dispatchOtherData,
  });

  // Track the original language when content is first generated
  useEffect(() => {
    if (!originalLanguage && (proposal.scope || proposal.assumptions || proposal.lawyers?.length > 0 || proposal.experience?.length > 0)) {
      setOriginalLanguage(language);
    }
  }, [originalLanguage, language, proposal.scope, proposal.assumptions, proposal.lawyers, proposal.experience]);

  // Track language changes to show regenerate button (only if not controlled externally)
  useEffect(() => {
    if (!externalShowRegenerate && originalLanguage && language !== originalLanguage) {
      setShowRegenerateButton(true);
    } else if (!externalShowRegenerate && originalLanguage && language === originalLanguage) {
      setShowRegenerateButton(false);
    }
  }, [language, originalLanguage, externalShowRegenerate]);

  const handleRegenerateClick = () => {
    if (onRegenerateClick) {
      onRegenerateClick();
    } else {
      setConfirmModalOpened(true);
    }
  };

  const handleRegenerateConfirm = async () => {
    setIsRegenerating(true);
    
    // First, wipe all generated content
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'scope',
      value: '',
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'assumptions',
      value: '',
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'lawyers',
      value: [],
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'experience',
      value: [],
    });
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'specialRoles',
      value: {},
    });
    
    // Set default fee text based on the target language
    const defaultFees = language === 'en' ? defaultFeeTextEn : defaultFeeTextPl;
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'fees',
      value: defaultFees,
    });
    
    const config = {
      input: proposal.input,
      sector: proposal.sector,
      documentLanguage: language,
      constraints: proposal.constraints,
      proposal: {
        ...proposal,
        scope: '',
        assumptions: '',
        lawyers: [],
        experience: [],
        specialRoles: {},
        fees: defaultFees,
      },
    };

    try {
      await generateAll(config);
      
      // Update the original language to the new language after successful regeneration
      setOriginalLanguage(language);
    } catch (error) {
      console.error('Error during regeneration:', error);
    } finally {
      setIsRegenerating(false);
      setShowRegenerateButton(false);
      setConfirmModalOpened(false);
    }
  };

  const handleDateBlur = () => {
    // only validate if it looks like a full YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      setDateError(t('invalidDate'));
      return;
    }
    setDateError(null);
    const parsed = new Date(dateString);
    if (isNaN(parsed.getTime())) {
      setDateError(t('invalidDate'));
      return;
    }
    console.log(parsed);
    // commit to your context once it's valid
    dispatchProposal({
      type: PROPOSALACTIONS.SET_FIELD,
      field: 'proposalDate',
      value: parsed,
    });
  };

  var displayDate = new Date();
  if (typeof proposalDate === 'string') {
    displayDate = new Date(proposalDate);
  } else if (proposalDate instanceof Date) {
    displayDate = proposalDate;
  }
  
  const shouldShowRegenerate = externalShowRegenerate !== undefined ? externalShowRegenerate : showRegenerateButton;
  
  return (
    <>
      <Title order={4}>{t('caseOverview')}</Title>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label={t('title')}
            size="sm"
            value={title}
            placeholder={t('addTitleHere')}
            onChange={(e) =>
              dispatchProposal({
                type: PROPOSALACTIONS.SET_FIELD,
                field: 'title',
                value: e.target.value,
              })
            }
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Group align="end" gap="xs">
            <div>
              <Text fz="sm" fw="500">
                {t('language')}
              </Text>
              <SegmentedControl
                value={language}
                onChange={(value) =>
                  dispatchProposal({
                    type: PROPOSALACTIONS.SET_FIELD,
                    field: 'language',
                    value,
                  })
                }
                data={[
                  { label: 'Polish', value: 'pl' },
                  { label: 'English', value: 'en' },
                ]}
              />
            </div>
            {shouldShowRegenerate && (
              <Button
                variant="light"
                color="cyan"
                size="xs"
                leftSection={<IconRotate2 size={14} />}
                onClick={handleRegenerateClick}
              >
                {t('regenerate')}
              </Button>
            )}
          </Group>
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={t('clientCompany')}
            style={{ width: '100%' }}
            value={client.company}
            onChange={(e) => {
              const newCompany = e.target.value;
              dispatchProposal({
                type: PROPOSALACTIONS.SET_FIELD,
                field: 'client',
                value: { ...client, company: newCompany },
              });
            }}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <Input.Wrapper label={t('clientRepresentative')}>
            <TextInput
              value={client.name}
              onChange={(e) => {
                const newName = e.target.value;
                dispatchProposal({
                  type: PROPOSALACTIONS.SET_FIELD,
                  field: 'client',
                  value: { ...client, name: newName },
                });
              }}
            />
          </Input.Wrapper>
        </Grid.Col>

        <Grid.Col span={6}>
          <Textarea
            label={t('clientSector')}
            value={sector || ''}
            onChange={(e) => {
              dispatchProposal({
                type: PROPOSALACTIONS.SET_FIELD,
                field: 'sector',
                value: e.target.value,
              });
            }}
            autosize
            minRows={1}
            maxRows={8}
            style={{ overflowY: 'auto' }}
            leftSectionWidth={1}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={t('proposalDate')}
            type="date"
            value={dateString}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDateString(e.target.value);
              // clear any previous error while typing
              setDateError(null);
            }}
            onBlur={handleDateBlur}
            error={dateError}
          />
        </Grid.Col>
      </Grid>

      {/* Regeneration Confirmation Modal */}
      <Modal
        opened={confirmModalOpened}
        onClose={() => setConfirmModalOpened(false)}
        title={t('confirmRegeneration')}
      >
        <Text>
          {t('regenerateWarning')}
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={() => setConfirmModalOpened(false)} disabled={isRegenerating}>
            {t('cancel')}
          </Button>
          <Button 
            color="cyan" 
            onClick={handleRegenerateConfirm} 
            disabled={isRegenerating}
            leftSection={isRegenerating ? <Loader size="xs" /> : undefined}
          >
            {isRegenerating ? t('loading') : t('confirm')}
          </Button>
        </Group>
      </Modal>
    </>
  );
}
